import React from 'react';
import type {MessageDescriptor} from 'react-intl';
import {defineMessages} from 'react-intl';
import type {
    DateRangeFilterValue,
    FilterGroupNewFilter,
    FilterProps,
    FiltersProps,
    MultiKeyFilterTextValue,
    QueryFilterAdapterModel,
    QueryFilterAdapterProps,
} from '@igp/shared/components/filter';
import {DateRangeFilter, FilterGroupNew, TextInputWithDropdownFilter, withQueryFilter} from '@igp/shared/components/filter';
import {CountryFilter} from '@igp/shared/features/country';
import {useLocale} from '@igp/shared/localization';
import type {Locale} from '@igp/shared/style';
import {getMirrorObject} from '@igp/shared/utils';
import {EntityType} from '@models';

import {firstAvailableDate} from '../constants';
import type {
    PayoutReportFilterKeys,
    PayoutReportFilterName,
    PayoutReportFilterViewModel,
    PayoutReportFilterViewModelTextKeys,
    PayoutReportTextFilterKeys,
} from '../types';

const localized = defineMessages({
    textFullFilter: {
        id: 'payout-report.filters.text-full-filter',
        defaultMessage: 'All',
    },
    player_uid: {
        id: 'payout-report.filters.player_uid',
        defaultMessage: 'Player ID',
    },
    player_username: {
        id: 'payout-report.filters.player_username',
        defaultMessage: 'Player Username',
    },
    agent_uid: {
        id: 'payout-report.filters.agent_uid',
        defaultMessage: 'Agent ID',
    },
    agent_username: {
        id: 'payout-report.filters.agent_username',
        defaultMessage: 'Agent Username',
    },
    numberPlaceholder: {
        id: 'payout-report.filters.number-placeholder',
        defaultMessage: 'For example, 123',
    },
    namePlaceholder: {
        id: 'payout-report.filters.name-placeholder',
        defaultMessage: 'For example, Antonio',
    },
    countries: {
        id: 'payout-report.filters.countries',
        defaultMessage: 'Country',
    },
});

function PayoutReportFilters({model, onChange, availableFilters}: FiltersProps<PayoutReportFilterViewModel, PayoutReportFilterName>) {
    const {locale} = useLocale();

    const availableDateRangeForSelection = {from: firstAvailableDate.toDate()};

    function getTextFilterPlaceholder(option: PayoutReportFilterViewModelTextKeys) {
        const placeholderMapper: Record<PayoutReportFilterViewModelTextKeys, MessageDescriptor> = {
            player_and_agent_uid_username: localized.numberPlaceholder,
            player_uid: localized.numberPlaceholder,
            player_username: localized.namePlaceholder,
            agent_uid: localized.numberPlaceholder,
            agent_username: localized.namePlaceholder,
        };
        return placeholderMapper[option];
    }

    const filters: FilterGroupNewFilter<PayoutReportFilterViewModel, PayoutReportFilterName>[] = [
        {
            filterName: 'player_and_agent_uid_username',
            modelField: 'player_and_agent_uid_username',
            component: (props: FilterProps<MultiKeyFilterTextValue<PayoutReportFilterViewModelTextKeys>>) => (
                <TextInputWithDropdownFilter
                    options={[
                        {label: localized.textFullFilter, value: 'player_and_agent_uid_username'},
                        {label: localized.player_uid, value: 'player_uid'},
                        {label: localized.player_username, value: 'player_username'},
                        {label: localized.agent_uid, value: 'agent_uid'},
                        {label: localized.agent_username, value: 'agent_username'},
                    ]}
                    getInputPlaceholder={getTextFilterPlaceholder}
                    width="full"
                    {...props}
                />
            ),
        },
        {
            filterName: 'countries',
            modelField: 'countries',
            component: (props: FilterProps<string[]>) => (
                <CountryFilter {...props} inputLabel={localized.countries} countryEntity={EntityType.Country} />
            ),
        },
        {
            filterName: 'transactionDate',
            modelField: 'transactionDate',
            component: (props: FilterProps<DateRangeFilterValue>) => (
                <DateRangeFilter
                    {...props}
                    width="full"
                    locale={locale as Locale}
                    availableDateRangeForSelection={availableDateRangeForSelection}
                    filterEntity="report"
                    hasInputTooltip={true}
                />
            ),
        },
    ];

    return (
        <FilterGroupNew
            mode="update-on-change"
            allFilters={filters}
            model={model}
            onChange={onChange}
            availableFilters={availableFilters}
        />
    );
}

type PayoutReportFiltersAdapterProps = QueryFilterAdapterProps<PayoutReportFilterKeys, PayoutReportFilterName, PayoutReportTextFilterKeys>;

function PayoutReportFiltersAdapter({model, onChange, ...props}: PayoutReportFiltersAdapterProps) {
    const viewModelTextKeyToQueryTextKey: Record<PayoutReportFilterViewModelTextKeys, PayoutReportTextFilterKeys> = {
        player_and_agent_uid_username: 'player_and_agent_uid_username',
        player_uid: 'player_uid',
        player_username: 'player_username',
        agent_uid: 'agent_uid',
        agent_username: 'agent_username',
    };
    const queryTextKeyToViewModelTextKey: Record<PayoutReportTextFilterKeys, PayoutReportFilterViewModelTextKeys> =
        getMirrorObject(viewModelTextKeyToQueryTextKey);

    const filterModel: PayoutReportFilterViewModel = {
        player_and_agent_uid_username: model?.text
            ? {
                  text: model.text.text,
                  option: queryTextKeyToViewModelTextKey[model?.text?.option],
              }
            : null,
        countries: model?.country as string[],
        transactionDate: {
            from: model?.transaction_date_from as number,
            to: model?.transaction_date_to as number,
        },
    };

    function handleChange(model: PayoutReportFilterViewModel) {
        const result: QueryFilterAdapterModel<PayoutReportFilterKeys, PayoutReportTextFilterKeys> = {
            text: model.player_and_agent_uid_username
                ? {
                      text: model.player_and_agent_uid_username.text,
                      option: viewModelTextKeyToQueryTextKey[model.player_and_agent_uid_username.option],
                  }
                : undefined,
            country: model.countries,
            transaction_date_from: model.transactionDate?.from,
            transaction_date_to: model.transactionDate?.to,
        };

        onChange(result);
    }

    return <PayoutReportFilters model={filterModel} onChange={handleChange} {...props} />;
}

export const PayoutReportQueryFilters = withQueryFilter<PayoutReportFilterKeys, PayoutReportFilterName, PayoutReportTextFilterKeys>(
    PayoutReportFiltersAdapter,
    {
        player_and_agent_uid_username: ['player_and_agent_uid_username', 'player_uid', 'player_username', 'agent_uid', 'agent_username'],
    }
);
